import { BulkInvitationRequest, CreateInvitationRequest, InvitationListParams, UserInvitation, userInvitationService } from '@/services/user-invitation.service';
import { userService } from '@/services/user.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format, isAfter, parseISO } from 'date-fns';
import { TableSkeleton } from '@/components/ui/SkeletonLoader';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  ChevronDown,
  Clock,
  Download,
  FileText,
  Hash,
  Info,
  Link,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  X,
  XCircle
} from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { Select } from '@/components/ui/Select';
import SimpleSelect from '@/components/ui/SimpleSelect';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import toast from 'react-hot-toast';
import Pagination from '@/components/ui/Pagination';
import { exportService } from '@/services/export.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { INVITE_MANAGEMENT_PERMISSION } from '@/constants/constant-permissions';

interface ActiveUser {
  _id: string;
  employee_id: string;
  full_name: string;
  email: string;
}

interface GroupRoleSelection {
  group_key: string;
  group_name: string;
  roles: Array<{ role_key: string; role_name: string }>;
}

interface InviteFormData {
  groups: GroupRoleSelection[];
  send_automatic_reminders: boolean;
  reminder_frequency_days: number;
  expiration_days: number;
  // Organizational Hierarchy
  reporting_manager_email: string;
  bu_head_email: string;
  hrbp_email: string;
}

// Free-text fields of the invite and add-department modals, rendered with the shared Input/Textarea
interface InviteFieldsFormData {
  invitations: Array<{ email: string; full_name: string }>;
  dept_name: string;
  dept_code: string;
  dept_description: string;
}

const EMPTY_INVITATION = { email: '', full_name: '' };

// Only characters real addresses use, so encoded payloads like "&#106&#97...@x.com" are rejected
const isValidEmail = (email: string): boolean =>
  /^[A-Za-z0-9._+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(email.trim());

interface InviteFilterFormData {
  search: string;
  status: string;
  sortBy: string;
}

export default function InviteManagement() {
  usePermissionRouteAccess([
    INVITE_MANAGEMENT_PERMISSION.ADMIN_INVITE_MANAGEMENT_READ,
    INVITE_MANAGEMENT_PERMISSION.ADMIN_INVITE_MANAGEMENT_WRITE,
  ]);
  const canWrite = useHasPermission([INVITE_MANAGEMENT_PERMISSION.ADMIN_INVITE_MANAGEMENT_WRITE]);

  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState<string[]>([]);

  // Filters with react-hook-form
  const { control: filterControl, watch: watchFilter } = useForm<InviteFilterFormData>({
    defaultValues: {
      search: '',
      status: 'all',
      sortBy: 'date',
    },
  });

  const searchTerm = watchFilter('search');
  const filterStatus = watchFilter('status') as 'all' | 'pending' | 'accepted' | 'registered' | 'expired' | 'cancelled' | 'inactive';
  const sortBy = watchFilter('sortBy') as 'date' | 'name' | 'status';

  // Debounce search term using reusable hook
  const debouncedSearchTerm = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);
  const [currentStep, setCurrentStep] = useState(1);
  const [_selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [invitationResult, setInvitationResult] = useState<any>(null);
  const [showResultStep, setShowResultStep] = useState(false);

  // Invite rows and add-department fields live in react-hook-form so the shared fields validate them
  const {
    control: inviteControl,
    getValues: getInviteValues,
    trigger: triggerInvite,
    reset: resetInviteFields,
    resetField: resetInviteField,
    watch: watchInvite,
  } = useForm<InviteFieldsFormData>({
    mode: 'onChange',
    defaultValues: { invitations: [EMPTY_INVITATION], dept_name: '', dept_code: '', dept_description: '' },
  });
  const { fields: invitationFields, append: appendInvitation, remove: removeInvitationRow } = useFieldArray({
    control: inviteControl,
    name: 'invitations',
  });
  const invitationRows = watchInvite('invitations');
  const departmentName = watchInvite('dept_name');
  const departmentCode = watchInvite('dept_code');
  const departmentDescription = watchInvite('dept_description');

  // Export state
  const [exportingInvitations, setExportingInvitations] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const [departments, setDepartments] = useState(['Engineering', 'Marketing', 'Sales', 'IT', 'Finance', 'HR', 'Operations']);

  const [inviteForm, setInviteForm] = useState<InviteFormData>({
    groups: [],
    send_automatic_reminders: true,
    reminder_frequency_days: 3,
    expiration_days: 7,
    reporting_manager_email: '',
    bu_head_email: '',
    hrbp_email: '',
  });

  // Active users for organizational hierarchy dropdowns
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Active users options for SimpleSelect
  const activeUserOptions = activeUsers.map(user => ({
    label: user.employee_id
      ? `${user.full_name} (${user.employee_id}) ${user.email}`
      : `${user.full_name} ${user.email}`,
    value: user.email
  }));

  // State for group-role selection
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<Array<{ role_key: string; role_name: string }>>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [loadingRoles, _setLoadingRoles] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus, sortBy]);

  // Fetch active users for reporting dropdowns when invite modal opens
  useEffect(() => {
    const fetchActiveUsers = async () => {
      if (!showInviteModal) return;

      setIsLoadingUsers(true);
      try {
        const response = await apiClient.get(API_ENDPOINTS.USERS.ACTIVE_USERS_FOR_REPORTING);
        if (response.data?.success && response.data?.data) {
          setActiveUsers(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching active users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchActiveUsers();
  }, [showInviteModal]);
 
  // Fetch invitations
  const { data: invitationsData, isLoading: loadingInvitations, refetch: refetchInvitations, error: invitationsError } = useQuery({
    queryKey: ['invitations', currentPage, pageLimit, filterStatus, debouncedSearchTerm, sortBy],
    queryFn: async () => {
      const params: InvitationListParams = {
        page: currentPage,
        limit: pageLimit,
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
        sort_by: sortBy === 'date' ? 'invited_at' : sortBy === 'name' ? 'full_name' : 'status',
        sort_order: sortBy === 'name' ? 'asc' : 'desc',
      };

      try {
        const response = await userInvitationService.getInvitations(params);
        if (response.success && response.data) {
          return response.data;
        }
        console.error('API response error:', response.error);
        throw new Error(response.error?.message || 'Failed to fetch invitations');
      } catch (error) {
        console.error('Query error:', error);
        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: true,
    staleTime: 5000, // Data is fresh for 5 seconds
  });

  // Fetch groups
  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      try {
        const response = await userService.getGroupsDropdown();
        // Handle different response structures
        if (Array.isArray(response)) {
          return response;
        } else if (response?.data && Array.isArray(response.data)) {
          return response.data;
        } else if (response?.groups && Array.isArray(response.groups)) {
          return response.groups;
        }
        // Fallback to empty array
        console.warn('Unexpected groups response structure:', response);
        return [];
      } catch (error) {
        console.error('Failed to fetch groups:', error);
        return [];
      }
    },
  });

  // Fetch metrics
  const { data: metrics } = useQuery({
    queryKey: ['invitation-metrics'],
    queryFn: async () => {
      const response = await userInvitationService.getInvitationMetrics();
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    },
  });

  // // Fetch roles for selected group
  // const fetchRolesForGroup = async (groupKey: string) => {
  //   setLoadingRoles(true);
  //   try {
  //     const response = await userService.getGroupRoles(groupKey);
  //     const roles = Array.isArray(response) ? response : 
  //                  response?.data ? response.data :
  //                  response?.roles ? response.roles : [];
  //     setAvailableRoles(roles);
  //   } catch (error) {
  //     console.error('Failed to fetch roles:', error);
  //     toast.error('Failed to load roles for this group');
  //     setAvailableRoles([]);
  //   } finally {
  //     setLoadingRoles(false);
  //   }
  // };

  // Handle group selection and role slectedby groupkey
  const handleGroupSelection = (groupKey: string) => {
    setSelectedGroupKey(groupKey);
    setSelectedRoles([]);

    if (groupKey) {

      const selectedGroup = groupsData?.find(
        (g: any) => g.group_key === groupKey
      );
      const rolesForGroup = selectedGroup ? selectedGroup.roles : [];

      setAvailableRoles(rolesForGroup);
    } else {

      setAvailableRoles([]);
    }
  };

  // Add group-role combination to the form
const addGroupRoleCombination = () => {
  if (!selectedGroupKey) {
    toast.error('Please select a department first.');
    return;
  }
  if (selectedRoles.length === 0) {
    toast.error('You must select at least one role for the department.');
    return;
  }

  const selectedGroup = groupsData?.find((g: any) => g.group_key === selectedGroupKey);
  if (!selectedGroup) return;

  const newGroupRole: GroupRoleSelection = {
    group_key: selectedGroupKey,
    group_name: selectedGroup.group_name || selectedGroup.name || selectedGroupKey,
    roles: [...selectedRoles]
  };

  setInviteForm(prev => ({
    ...prev,
    groups: [...prev.groups, newGroupRole]
  }));

  // Reset selection
  setSelectedGroupKey('');
  setSelectedRoles([]);
  setAvailableRoles([]);
};
  // Remove group-role combination
  const removeGroupRoleCombination = (index: number) => {
    setInviteForm(prev => ({
      ...prev,
      groups: prev.groups.filter((_, i) => i !== index)
    }));
  };

  // Create invitations mutation
  const createInvitationsMutation = useMutation({
    mutationFn: async (data: BulkInvitationRequest) => {
      const response = await userInvitationService.createInvitations(data);
      if (!response.success) {
        // Handle both error formats
        const errorMessage = response.error?.error_description ||
          response.error?.message ||
          response.description ||
          'Failed to send invitations';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Set the result and show result step
      setInvitationResult(data);
      setShowResultStep(true);
      setCurrentStep(3); // Move to result step

      // Only show success toast if all succeeded
      if (data?.failed === 0 || !data?.failed) {
        toast.success(`Successfully sent ${data?.success || data?.total || 0} invitation(s)!`);
      } else if ((data?.success ?? 0) > 0 && (data?.failed ?? 0) > 0) {
        toast(`⚠️ Sent ${data.success} invitation(s), ${data.failed} failed`, {
          style: {
            background: '#FEF3C7',
            color: '#92400E',
          },
        });
      } else if (data?.failed > 0) {
        toast.error(`Failed to send ${data.failed} invitation(s)`);
      }

      // Refresh the list
      refetchInvitations();
      queryClient.invalidateQueries({ queryKey: ['invitation-metrics'] });
    },
    onError: (error: any) => {
      // Backend validation errors (NestJS ValidationPipe) arrive at data.message as a string OR an
      // array, e.g. ["invitations.0.Disposable or temporary email addresses are not allowed"].
      // Normalise to a clean string and strip the "invitations.N." field-path prefix so the user
      // sees a readable reason instead of "Unknown / Bad Request".
      const rawMessage = error?.response?.data?.message;
      const normalisedMessage = Array.isArray(rawMessage)
        ? rawMessage.map((m: string) => String(m).replace(/^invitations\.\d+\./, '').trim()).join('; ')
        : rawMessage;

      const errorMessage = error?.response?.data?.error?.error_description ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error_description ||
        normalisedMessage ||
        error?.message ||
        'Failed to send invitations';

      toast.error(errorMessage);

      // Show error in modal as well — include email + reason so the result step can render a
      // meaningful row instead of "Unknown / Bad Request".
      setInvitationResult({
        success: 0,
        failed: getInviteValues('invitations').length,
        errors: [{
          email: getInviteValues('invitations')?.[0]?.email,
          message: errorMessage,
          reason: errorMessage,
        }]
      });
      setShowResultStep(true);
      setCurrentStep(3);
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await userInvitationService.resendInvitation(invitationId);
      if (!response.success) {
        const errorMessage = response.error?.error_description ||
          response.error?.message ||
          response.description ||
          'Failed to resend invitation';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success('Invitation resent successfully!');
      // Invalidate queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['invitations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['invitation-metrics'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to resend invitation';
      toast.error(errorMessage);
    },
  });

  // Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await userInvitationService.cancelInvitation(invitationId);
      if (!response.success) {
        const errorMessage = response.error?.error_description ||
          response.error?.message ||
          response.description ||
          'Failed to cancel invitation';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success('Invitation cancelled successfully!');
      // Invalidate queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['invitations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['invitation-metrics'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to cancel invitation';
      toast.error(errorMessage);
    },
  });

  // Bulk resend mutation
  const bulkResendMutation = useMutation({
    mutationFn: async (invitationIds: string[]) => {
      const response = await userInvitationService.bulkResendInvitations({ invitation_ids: invitationIds });
      if (!response.success) {
        const errorMessage = response.error?.error_description ||
          response.error?.message ||
          response.description ||
          'Failed to resend invitations';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.success && data.success > 0) {
        toast.success(`Successfully resent ${data.success} invitation(s)`);
      }
      if (data?.failed && data.failed > 0) {
        toast.error(`Failed to resend ${data.failed} invitation(s)`);
      }
      setSelectedInvites([]);
      // Invalidate queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['invitations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['invitation-metrics'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to resend invitations';
      toast.error(errorMessage);
    },
  });
  // Bulk cancel mutation
  const bulkCancelMutation = useMutation({
    mutationFn: async (invitationIds: string[]) => {
      const response = await userInvitationService.bulkCancelInvitations({ invitation_ids: invitationIds });
      if (!response.success) {
        const errorMessage = response.error?.error_description ||
          response.error?.message ||
          response.description ||
          'Failed to cancel invitations';
        throw new Error(errorMessage);
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.success && data.success > 0) {
        toast.success(`Successfully cancelled ${data.success} invitation(s)`);
      }
      if (data?.failed && data.failed > 0) {
        toast.error(`Failed to cancel ${data.failed} invitation(s)`);
      }
      setSelectedInvites([]);
      // Invalidate queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['invitations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['invitation-metrics'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to cancel invitations';
      toast.error(errorMessage);
    },
  });

  // Handle click outside for export dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle export functionality using export service
  const exportInvitations = async (format: 'csv' | 'excel') => {
    try {
      setExportingInvitations(true);
      setShowExportDropdown(false);

      // Map UI sortBy values to API sort_by values
      const sortByMap: Record<string, 'invited_at' | 'full_name' | 'status'> = {
        'date': 'invited_at',
        'name': 'full_name',
        'status': 'status'
      };

      // Use export service to call the export API with filters only (no pagination)
      const result = await exportService.exportInvitations(format, {
        search: debouncedSearchTerm || undefined,
        status: filterStatus,
        sort_by: sortByMap[sortBy] || 'invited_at',
        sort_order: sortBy === 'name' ? 'asc' : 'desc'
      });

      if (result.success) {
        toast.success(`Successfully exported ${result.count} invitations to ${format.toUpperCase()}`);
      } else {
        toast.error(result.message || 'No invitations found to export');
      }
    } catch (error: any) {
      console.error('Export error details:', error);
      let errorMessage = 'Failed to export invitations';

      if (error?.response?.status === 404) {
        errorMessage = 'Export feature is not available at this time';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred during export. Please try again later.';
      } else if (error?.message) {
        errorMessage = `Export failed: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setExportingInvitations(false);
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'registered':
        return <UserCheck className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-400" />;
      case 'inactive':
        return <UserX className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'accepted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'registered':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'expired':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'inactive':
        return 'bg-gray-50 text-gray-500 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handleSendInvite = async () => {
    // Runs the shared fields' rules (email format, required name, clean text); fields show their own errors
    if (!(await triggerInvite('invitations'))) {
      toast.error('Please fix the user details before sending.');
      return;
    }
    const validInvitations = getInviteValues('invitations').filter(inv => inv.email.trim());

    if (validInvitations.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

   if (inviteForm.groups.length === 0) {
  if (!selectedGroupKey) {
    toast.error('Please select a Department/Group to continue.');
    return;
  }

  if (selectedRoles.length === 0) {
    toast.error('Please select at least one role for the chosen department.');
    return;
  }

  toast.error("Please click the '+ Add Group & Roles' button to confirm your selection.");
  return; 
}
    

    const request: BulkInvitationRequest = {
      invitations: validInvitations.map(inv => ({
        email: inv.email.trim(),
        full_name: inv.full_name.trim() || inv.email.split('@')[0],
        groups: inviteForm.groups,
        // Organizational Hierarchy
        ...(inviteForm.reporting_manager_email && { reporting_manager_email: inviteForm.reporting_manager_email }),
        ...(inviteForm.bu_head_email && { bu_head_email: inviteForm.bu_head_email }),
        ...(inviteForm.hrbp_email && { hrbp_email: inviteForm.hrbp_email }),
      })),
      invitation_config: {
        send_automatic_reminders: inviteForm.send_automatic_reminders,
        reminder_frequency_days: inviteForm.reminder_frequency_days,
        expiration_days: inviteForm.expiration_days,
      }
    };

    createInvitationsMutation.mutate(request);
  };

  const resetInviteForm = () => {
    setInviteForm({
      groups: [],
      send_automatic_reminders: true,
      reminder_frequency_days: 3,
      expiration_days: 7,
      reporting_manager_email: '',
      bu_head_email: '',
      hrbp_email: '',
    });
    resetInviteFields();
    setCurrentStep(1);
    setSelectedGroups([]);
    setSelectedGroupKey('');
    setSelectedRoles([]);
    setAvailableRoles([]);
    setInvitationResult(null);
    setShowResultStep(false);
  };

  const nextStep = async () => {
  if (currentStep === 1 && !(await triggerInvite('invitations'))) {
    toast.error('Please fill out all required fields correctly.');
    return;
  }

    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return 'User Details';
      case 2: return 'Department, Roles & Settings';
      case 3: return 'Invitation Results';
      default: return 'Step ' + step;
    }
  };

  const handleResendInvite = (id: string) => {
    resendInvitationMutation.mutate(id);
  };

  const handleCancelInvite = (id: string) => {
    cancelInvitationMutation.mutate(id);
  };

  const handleBulkResend = () => {
    if (selectedInvites.length === 0) {
      toast.error('Please select invitations to resend');
      return;
    }
    bulkResendMutation.mutate(selectedInvites);
  };

  const handleBulkCancel = () => {
    if (selectedInvites.length === 0) {
      toast.error('Please select invitations to resend');
      return;
    }
    bulkCancelMutation.mutate(selectedInvites);
  };
  const handleCopyLink = (invite: UserInvitation) => {
    // In real app, construct the actual invitation link with token
    const inviteLink = `${window.location.origin}/register?token=${invite.invitation_token}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invitation link copied to clipboard!');
  };

  const closeAddDepartmentModal = () => {
    setShowAddDepartmentModal(false);
    resetInviteField('dept_name');
    resetInviteField('dept_code');
    resetInviteField('dept_description');
  };

  // Helper functions for managing invitations array
  const removeInvitation = (index: number) => {
    if (invitationFields.length > 1) {
      removeInvitationRow(index);
    }
  };
  const handleAddUserClick = async () => {
    // Validate the current last row before adding another
    if (await triggerInvite(`invitations.${invitationFields.length - 1}`)) {
      appendInvitation(EMPTY_INVITATION);
    } else {
      toast.error('Please fill out the current user before adding another.');
    }
  };
  // Email placeholder function removed - no longer needed

  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      // headers line skipped — data starts at index 1

      const invitations: CreateInvitationRequest[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= 2) {
          const invitation: CreateInvitationRequest = {
            email: values[0],
            full_name: values[1],
            groups: values[2] ? values[2].split(';').map(g => ({
              group_key: g.trim(),
              group_name: g.trim(),
              roles: []
            })) : [],
          };
          invitations.push(invitation);
        }
      }

      if (invitations.length > 0) {
        const request: BulkInvitationRequest = {
          invitations,
          invitation_config: {
            send_automatic_reminders: true,
            reminder_frequency_days: 3,
            expiration_days: 7,
          }
        };

        createInvitationsMutation.mutate(request);
        setShowBulkInviteModal(false);
      } else {
        toast.error('No valid invitations found in CSV');
      }
    };

    reader.readAsText(file);
  };

  // Handle different response structures with better error handling
  const invitations = React.useMemo(() => {
    if (!invitationsData) return [];

    if (Array.isArray(invitationsData)) {
      return invitationsData;
    }

    if (invitationsData.invitations && Array.isArray(invitationsData.invitations)) {
      return invitationsData.invitations;
    }

    if ((invitationsData as any).data && Array.isArray((invitationsData as any).data)) {
      return (invitationsData as any).data;
    }

    // If we have data but it's not in expected format, log it for debugging
    console.warn('Unexpected invitations data structure:', invitationsData);
    return [];
  }, [invitationsData]);

 const totalPages = React.useMemo(() => {
    if (!invitationsData) {
      return 1;
    }
    const totalItems = invitationsData?.total;
    if (totalItems && pageLimit > 0) {
      return Math.ceil(totalItems / pageLimit);
    }
    
    return 1;

}, [invitationsData, pageLimit]); 

  const stats = {
    total: metrics?.total_invited || 0,
    pending: metrics?.pending || 0,
    accepted: metrics?.accepted || 0,
    registered: metrics?.registered || 0,
    expired: metrics?.expired || 0,
  };

  const acceptanceRate = metrics?.success_rate ? metrics.success_rate.toFixed(1) : '0';

  // Show loading only for initial load, not when searching
  const isInitialLoading = loadingInvitations && !searchTerm && debouncedSearchTerm === '';

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-white/20 rounded w-48 mb-2"></div>
            <div className="h-4 bg-white/20 rounded w-64"></div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white/10 rounded-lg p-3">
                  <div className="h-3 bg-white/20 rounded w-16 mb-2"></div>
                  <div className="h-6 bg-white/20 rounded w-12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Table Skeleton */}
        <div className="card">
          <TableSkeleton rows={10} columns={7} showHeader={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">User Invitations</h1>
            <p className="text-brand-purple-100">
              Manage and track user invitations to your organization
            </p>
          </div>
          <div className="flex gap-3">
            {/* <button
              onClick={() => setShowBulkInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span className="hidden sm:inline">Bulk Import</span>
            </button> */}
            <button
              onClick={() => canWrite && setShowInviteModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-brand-purple-600 rounded-lg transition-colors ${
                canWrite ? 'hover:bg-brand-purple-50' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <UserPlus className="w-5 h-5" />
              <span className="hidden sm:inline">Invite Users</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-purple-100 text-sm">Total Invites</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-purple-100 text-sm">Pending</p>
            <p className="text-2xl font-bold">{stats.pending}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-purple-100 text-sm">Accepted</p>
            <p className="text-2xl font-bold">{stats.accepted}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-purple-100 text-sm">Registered</p>
            <p className="text-2xl font-bold">{stats.registered}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-purple-100 text-sm">Success Rate</p>
            <p className="text-2xl font-bold">{acceptanceRate}%</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            {loadingInvitations && searchTerm !== debouncedSearchTerm && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-600 animate-spin z-10" />
            )}
            <Controller
              name="search"
              control={filterControl}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Search invitations by email"
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              )}
            />
          </div>

          <div className="flex gap-2">
            <div className="min-w-[140px]">
              <Select<InviteFilterFormData>
                name="status"
                control={filterControl}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'registered', label: 'Registered' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="All Status"
              />
            </div>

            <div className="min-w-[140px]">
              <Select<InviteFilterFormData>
                name="sortBy"
                control={filterControl}
                options={[
                  { value: 'date', label: 'Sort by Date' },
                  { value: 'name', label: 'Sort by Name' },
                  { value: 'status', label: 'Sort by Status' }
                ]}
                placeholder="Sort by Date"
              />
            </div>

            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => canWrite && setShowExportDropdown(!showExportDropdown)}
                disabled={!canWrite || exportingInvitations}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : 'Export invitations'}
              >
                {exportingInvitations ? (
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {exportingInvitations ? 'Exporting...' : 'Export'}
                </span>
                {!exportingInvitations && <ChevronDown className="w-4 h-4" />}
              </button>

              {showExportDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg z-10 border border-gray-200">
                  <button
                    onClick={() => exportInvitations('csv')}
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Export as CSV</span>
                  </button>
                  <button
                    onClick={() => exportInvitations('excel')}
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-b-lg"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Export as Excel</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">
                  {/* <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedInvites(invitations.map(i => i.invitation_id));
                      } else {
                        setSelectedInvites([]);
                      }
                    }}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  /> */}
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Departments</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Invited</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Expires</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invite: any) => (
                <tr key={invite.invitation_id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    {/* <input
                      type="checkbox"
                      checked={selectedInvites.includes(invite.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInvites([...selectedInvites, invite.id]);
                        } else {
                          setSelectedInvites(selectedInvites.filter(id => id !== invite.id));
                        }
                      }}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    /> */}
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">{invite.full_name}</p>
                      <p className="text-sm text-gray-600">{invite.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      {invite.groups && invite.groups.length > 0 ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">
                            {invite.groups.map((g: any) => g.group_name).join(', ')}
                          </p>
                          {invite.custom_metadata?.department && (
                            <p className="text-xs text-gray-600">{invite.custom_metadata.department}</p>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">No groups assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(invite.status)}`}>
                      {getStatusIcon(invite.status)}
                      {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                    </span>
                    {invite.reminder_count > 0 && invite.status === 'pending' && (
                      <p className="text-xs text-gray-500 mt-1">
                        {invite.reminder_count} reminder{invite.reminder_count > 1 ? 's' : ''} sent
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm">
                      <p className="text-gray-900">{invite.invited_at ? format(parseISO(invite.invited_at), 'MMM dd, yyyy') : 'N/A'}</p>
                      <p className="text-xs text-gray-500">by {invite.invited_by_name}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm">
                      {invite.status === 'registered' && invite.registered_at ? (
                        <div>
                          <p className="text-green-600 font-medium">Registered</p>
                          <p className="text-xs text-gray-500">{invite.registered_at ? format(parseISO(invite.registered_at), 'MMM dd, yyyy') : 'N/A'}</p>
                        </div>
                      ) : invite.status === 'accepted' && invite.accepted_at ? (
                        <div>
                          <p className="text-blue-600 font-medium">Accepted</p>
                          <p className="text-xs text-gray-500">{invite.accepted_at ? format(parseISO(invite.accepted_at), 'MMM dd, yyyy') : 'N/A'}</p>
                        </div>
                      ) : (
                        <div>
                          {(() => {
                            // Support both expires_at and invitation_expires_at field names
                            const expiresAt = invite.expires_at || (invite as any).invitation_expires_at;
                            const isExpired = expiresAt && isAfter(new Date(), parseISO(expiresAt));
                            const daysLeft = expiresAt ? differenceInDays(parseISO(expiresAt), new Date()) : null;

                            return (
                              <>
                                <p className={isExpired ? 'text-red-600' : 'text-gray-900'}>
                                  {expiresAt ? format(parseISO(expiresAt), 'MMM dd, yyyy') : 'N/A'}
                                </p>
                                <p className={`text-xs ${isExpired ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                  {isExpired
                                    ? 'Expired'
                                    : daysLeft !== null
                                      ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`
                                      : 'N/A'}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-1">
                      {(invite.status === 'pending' || invite.status === 'accepted') && (
                        <>
                          <button
                            onClick={() => canWrite && handleResendInvite(invite.id)}
                            disabled={!canWrite}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!canWrite ? 'You do not have permission' : 'Resend Invite'}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => canWrite && handleCopyLink(invite)}
                            disabled={!canWrite}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!canWrite ? 'You do not have permission' : 'Copy Link'}
                          >
                            <Link className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {(invite.status === 'pending' || invite.status === 'accepted') && (
                        <button
                          onClick={() => canWrite && handleCancelInvite(invite.id)}
                          disabled={!canWrite}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!canWrite ? 'You do not have permission' : 'Cancel Invite'}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      {/* <button
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button> */}
                      {/* <button
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="More Options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Error State */}
          {invitationsError && (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Failed to load invitations</p>
              <p className="text-sm text-gray-500 mb-4">
                {invitationsError.message || 'Something went wrong. Please try again.'}
              </p>
              <button
                onClick={() => refetchInvitations()}
                className="btn btn-primary btn-sm"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Loading State for Search */}
          {loadingInvitations && searchTerm !== debouncedSearchTerm && (
              <TableSkeleton rows={5} columns={7} showHeader={false} />
          )}

          {/* Empty State */}
          {!invitationsError && !loadingInvitations && invitations.length === 0 && (
            <div className="text-center py-12">
              <UserX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No invitations found</p>
              <p className="text-sm text-gray-500">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your filters or search terms'
                  : 'Send your first invitation to get started'}
              </p>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedInvites.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-gray-50 border-t">
            <span className="text-sm text-gray-600">
              {selectedInvites.length} invitation{selectedInvites.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => canWrite && handleBulkResend()}
                disabled={!canWrite}
                className="btn btn-sm btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : undefined}
              >
                Resend Selected
              </button>
              <button
                onClick={() => canWrite && handleBulkCancel()}
                disabled={!canWrite}
                className="btn btn-sm btn-outline text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : undefined}
              >
                Cancel Selected
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={invitationsData?.total || 0}
            itemsPerPage={pageLimit}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newLimit) => {
              setPageLimit(newLimit);
              setCurrentPage(1);
            }}
            showItemsPerPage={true}
          />
        )}
      </div>

      {/* Multi-Step Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <UserPlus className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Invite New Users</h2>
                    <p className="text-sm text-gray-600">Step {currentStep} of {showResultStep ? 3 : 2}: {getStepTitle(currentStep)}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    resetInviteForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Progress Steps */}
              <div className="flex items-center mt-4">
                {(showResultStep ? [1, 2, 3] : [1, 2]).map((step) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${step <= currentStep
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                      }`}>
                      {step}
                    </div>
                    {step < (showResultStep ? 3 : 2) && (
                      <div className={`flex-1 h-0.5 mx-2 ${step < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                        }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Step 1: User Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>

                    {/* Individual User Invitations */}
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Users to Invite <span className="text-red-500">*</span>
                      </label>

                      {invitationFields.map((invitation, index) => (
                        <div key={invitation.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              User {index + 1}
                            </h4>
                            {invitationFields.length > 1 && (
                              <button
                                onClick={() => removeInvitation(index)}
                                className="text-red-600 hover:bg-red-100 p-1 rounded"
                                title="Remove user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Email */}
                            <Input<InviteFieldsFormData>
                              name={`invitations.${index}.email`}
                              control={inviteControl}
                              type="email"
                              label="Email Address"
                              required
                              placeholder="user@example.com"
                              className="bg-white"
                              rules={{
                                required: 'A valid email is required.',
                                validate: (value: string) => isValidEmail(value) || 'Please enter a valid email format.',
                              }}
                            />

                            {/* Name */}
                            <Input<InviteFieldsFormData>
                              name={`invitations.${index}.full_name`}
                              control={inviteControl}
                              label="Full Name"
                              required
                              placeholder="John Doe"
                              className="bg-white"
                              rules={{
                                validate: (value: string) => !!value?.trim() || 'Full name is required.',
                              }}
                            />
                          </div>
                        </div>
                      ))}

                      {/* Add Another User Button */}
                      <button
                        onClick={handleAddUserClick}
                        className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <UserPlus className="w-5 h-5" />
                        <span>Add Another User</span>
                      </button>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Tips:</p>
                            <ul className="space-y-1 text-xs">
                              <li>• Each user will receive their own personalized invitation</li>
                              <li>• You can assign the same groups and department to all users</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Department & Role Assignment */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Department & Role Assignment</h3>

                  {/* Group-Role Selection Section */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Department/Group Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Department/Group <span className="text-red-500">*</span>
                        </label>
                        <SimpleSelect
                          value={selectedGroupKey}
                          onChange={(value) => handleGroupSelection(String(value || ''))}
                          options={Array.isArray(groupsData) ? groupsData
                            .filter((group: any) => !inviteForm.groups.some(g => g.group_key === group.group_key))
                            .map((group: any) => ({ value: group.group_key, label: group.group_name || group.name })) : []}
                          placeholder="Select a department..."
                          isClearable
                        />
                        {inviteForm.groups.length === groupsData?.length && (
                          <p className="text-xs text-amber-600 mt-1">All available groups have been assigned</p>
                        )}
                      </div>

                      {/* Role Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Roles <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <SimpleSelect
                            isMulti
                            isDisabled={!selectedGroupKey || loadingRoles}
                            values={selectedRoles.map(r => r.role_key)}
                            onValuesChange={(keys) => {
                              const roles = availableRoles
                                .filter((role: any) => keys.includes(role.role_key))
                                .map((role: any) => ({
                                  role_key: role.role_key,
                                  role_name: role.role_name || role.name
                                }));
                              setSelectedRoles(roles);
                            }}
                            options={availableRoles.map((role: any) => ({ value: role.role_key, label: role.role_name || role.name }))}
                            placeholder={loadingRoles ? 'Loading roles...' : selectedGroupKey ? 'Select roles...' : 'Select a department first'}
                            noOptionsMessage={() => 'No roles available'}
                          />
                          {loadingRoles && (
                            <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">You can select multiple roles</p>
                      </div>
                    </div>

                    {/* Add Button */}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={addGroupRoleCombination}
                        disabled={!selectedGroupKey || selectedRoles.length === 0}
                        className="btn btn-outline btn-sm flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Group & Roles
                      </button>
                    </div>
                  </div>

                  {/* Added Group-Role Combinations */}
                  {inviteForm.groups.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Assigned Groups & Roles
                      </label>
                      <div className="space-y-3">
                        {inviteForm.groups.map((groupRole, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Building2 className="w-5 h-5 text-gray-600" />
                                  <span className="font-medium text-gray-900">{groupRole.group_name}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {groupRole.roles.map((role, roleIndex) => (
                                    <span
                                      key={roleIndex}
                                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                                    >
                                      {role.role_name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeGroupRoleCombination(index)}
                                className="ml-3 text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Organizational Hierarchy (Optional) */}
                  <div className="border-t pt-6">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary-600" />
                      Organizational Hierarchy
                      <span className="text-sm font-normal text-gray-500">(Optional)</span>
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Assign reporting structure for the invited user(s).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      
                      <SimpleSelect
                        label="BU Head"
                        value={inviteForm.bu_head_email}
                        onChange={(value) => setInviteForm({ ...inviteForm, bu_head_email: String(value || '') })}
                        options={activeUserOptions}
                        placeholder={isLoadingUsers ? "Loading..." : "Select BU Head"}
                        isSearchable={true}
                        isClearable
                        isDisabled={isLoadingUsers}
                      />
                      <SimpleSelect
                        label="Reporting Manager"
                        value={inviteForm.reporting_manager_email}
                        onChange={(value) => setInviteForm({ ...inviteForm, reporting_manager_email: String(value || '') })}
                        options={activeUserOptions}
                        placeholder={isLoadingUsers ? "Loading..." : "Select manager"}
                        isSearchable={true}
                        isClearable
                        isDisabled={isLoadingUsers}
                      />
                      <SimpleSelect
                        label="HRBP"
                        value={inviteForm.hrbp_email}
                        onChange={(value) => setInviteForm({ ...inviteForm, hrbp_email: String(value || '') })}
                        options={activeUserOptions}
                        placeholder={isLoadingUsers ? "Loading..." : "Select HRBP"}
                        isSearchable={true}
                        isClearable
                        isDisabled={isLoadingUsers}
                      />
                    </div>
                  </div>

                  {/* Invitation Settings */}
                  <div className="border-t pt-6">
                    <h4 className="font-medium text-gray-900 mb-4">Invitation Settings</h4>

                    <div className="space-y-4">
                      {/* Automatic Reminders */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="font-medium text-gray-700">Send automatic reminders</label>
                          <p className="text-sm text-gray-500">Remind users who haven't accepted</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inviteForm.send_automatic_reminders}
                            onChange={(e) => setInviteForm({ ...inviteForm, send_automatic_reminders: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Reminder Frequency */}
                      {inviteForm.send_automatic_reminders && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reminder frequency
                          </label>
                          <SimpleSelect
                            value={inviteForm.reminder_frequency_days}
                            onChange={(value) => setInviteForm({ ...inviteForm, reminder_frequency_days: Number(value) })}
                            options={[
                              { value: 1, label: 'Daily' },
                              { value: 3, label: 'Every 3 days' },
                              { value: 7, label: 'Weekly' },
                              { value: 14, label: 'Every 2 weeks' },
                            ]}
                            isSearchable={false}
                          />
                        </div>
                      )}

                      {/* Expiration */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Invitation expires in
                        </label>
                        <SimpleSelect
                          value={inviteForm.expiration_days}
                          onChange={(value) => setInviteForm({ ...inviteForm, expiration_days: Number(value) })}
                          options={[
                            { value: 7, label: '7 days' },
                            { value: 14, label: '14 days' },
                            { value: 30, label: '30 days' },
                            { value: 60, label: '60 days' },
                            { value: 90, label: '90 days' },
                          ]}
                          isSearchable={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Results */}
              {currentStep === 3 && showResultStep && invitationResult && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Invitation Results</h3>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">{invitationResult.total || 0}</div>
                      <div className="text-sm text-gray-600">Total Invitations</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{invitationResult.success || 0}</div>
                      <div className="text-sm text-gray-600">Sent Successfully</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{invitationResult.failed || 0}</div>
                      <div className="text-sm text-gray-600">Failed</div>
                    </div>
                  </div>

                  {/* Success List */}
                  {invitationResult.invitations && invitationResult.invitations.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Successfully Sent Invitations
                      </h4>
                      <div className="space-y-2">
                        {invitationResult.invitations.map((inv: any, index: number) => (
                          <div key={inv.invitation_id || index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <UserCheck className="w-4 h-4 text-green-600" />
                              <div>
                                <div className="font-medium text-gray-900">{inv.full_name || inv.email}</div>
                                <div className="text-sm text-gray-600">{inv.email}</div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {inv.invitation_id}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Error List */}
                  {invitationResult.errors && invitationResult.errors.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        Failed Invitations
                      </h4>
                      <div className="space-y-2">
                        {invitationResult.errors.map((error: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <UserX className="w-4 h-4 text-red-600" />
                              <div>
                                <div className="font-medium text-gray-900">{error.email || 'Unknown'}</div>
                                <div className="text-sm text-red-600">{error.error || error.message || 'Failed to send invitation'}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-center gap-3 pt-4">
                    <button
                      onClick={() => {
                        resetInviteForm();
                      }}
                      className="btn btn-outline"
                    >
                      Send More Invitations
                    </button>
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        resetInviteForm();
                      }}
                      className="btn btn-primary"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-between">
              <div>
              </div>

              <div className="flex gap-3">
                {currentStep > 1 && currentStep < 3 && (
                  <button onClick={prevStep} className="btn btn-outline" type="button">
                    Previous
                  </button>
                )}

                {currentStep < 3 && (
                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      resetInviteForm();
                    }}
                    className="btn btn-outline"
                    type="button"
                  >
                    Cancel
                  </button>
                )}

                {currentStep === 1 && (
                  <button
                    onClick={nextStep}
                    // disabled={!inviteForm.invitations.some(i => i.email.trim())}
                    className="btn btn-primary"
                    type="button"
                  >
                    Next Step
                  </button>
                )}

                {currentStep === 2 && (
                  <button
                    onClick={handleSendInvite}
                    // disabled={!inviteForm.invitations.some(i => i.email.trim()) || inviteForm.groups.length === 0 || createInvitationsMutation.isPending}
                    className="btn btn-primary flex items-center gap-2"
                    type="button"
                  >
                    {createInvitationsMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Invitation{invitationRows.filter(i => i.email.trim()).length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal - Removed */}

      {/* Bulk Import Modal */}
      {showBulkInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Bulk Import Users</h2>
              <p className="text-sm text-gray-600 mt-1">Upload a CSV file to invite multiple users</p>
            </div>

            <div className="p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Drop your CSV file here, or click to browse</p>
                <p className="text-xs text-gray-500 mb-4">
                  File should contain columns: email, full_name, groups (semicolon-separated)
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  id="csvUpload"
                  onChange={handleCSVUpload}
                />
                <label htmlFor="csvUpload" className="btn btn-primary cursor-pointer">
                  Choose File
                </label>
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <a
                    href="/templates/user-invitations-template.csv"
                    download
                    className="text-primary-600 hover:underline"
                  >
                    Download sample CSV template
                  </a>
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowBulkInviteModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {showAddDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay"> 
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <Building2 className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Add New Department</h2>
                    <p className="text-sm text-gray-600">Create a new department for your organization</p>
                  </div>
                </div>
                <button
                  onClick={closeAddDepartmentModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input<InviteFieldsFormData>
                      name="dept_name"
                      control={inviteControl}
                      label="Department Name"
                      required
                      placeholder="e.g., Product Development"
                    />

                    <div className="relative">
                      <Input<InviteFieldsFormData>
                        name="dept_code"
                        control={inviteControl}
                        label="Department Code"
                        required
                        placeholder="e.g., PROD-DEV"
                        className="pl-10 sm:pl-10 uppercase"
                      />
                      <Hash className="absolute left-3 top-[2.65rem] -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Textarea<InviteFieldsFormData>
                      name="dept_description"
                      control={inviteControl}
                      label="Description"
                      placeholder="Brief description of the department's responsibilities..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Preview */}
                {departmentName && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Department Preview</h4>
                    <div className="flex items-start gap-3">
                      <Building2 className="w-10 h-10 text-blue-600 bg-white rounded-lg p-2 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-900">
                          {departmentName}
                          {departmentCode && (
                            <span className="ml-2 text-xs bg-blue-100 px-2 py-0.5 rounded">
                              {departmentCode.toUpperCase()}
                            </span>
                          )}
                        </p>
                        {departmentDescription && (
                          <p className="text-sm text-blue-700 mt-1">{departmentDescription}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={closeAddDepartmentModal}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Runs the shared fields' clean-text rule; the fields show their own errors
                  if (!(await triggerInvite(['dept_name', 'dept_code', 'dept_description']))) return;

                  // Add the new department to the list
                  setDepartments([...departments, departmentName.trim()]);

                  // If this department was just created, select it
                  setInviteForm({ ...inviteForm } as any);

                  closeAddDepartmentModal();
                }}
                disabled={!departmentName.trim() || !departmentCode.trim()}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Department
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}